import { useEffect, useState } from "react";
import { MembershipList, InviteList, CreateInviteForm, type MembershipRequest } from "@/modules/membership";
import { useMemberships } from "@/modules/membership";
import { useAuth, usePermission } from "@/modules/auth";
import { usePlan, useBrands } from "@/modules/brand";

import MembershipListSkeleton from "@/modules/membership/components/membership-list-skeleton";
import InviteListSkeleton from "@/modules/membership/components/invite-list-skeleton";
import { TeamPlanRestrictionCallout } from "@/modules/membership/components/team-plan-restriction";
import { DeleteConfirmationModal } from "@/components/ui/delete-confirmation-modal";
import { useToast } from "@/hooks/use-toast";
import Container from "@/components/ui/container";
import { useOnboardingStore } from "@/modules/onboarding/stores/onboarding-store";

export default function TeamsPage() {
	const { createMembership, indexMembership, deleteMembership, resendMembershipInvitation, memberships, invites } = useMemberships();
	const [isLoading, setIsLoading] = useState(false);
	const { currentWorkspaceId } = useAuth();
	const { showToast } = useToast();
	const [inviteIsLoading, setInviteIsLoading] = useState(false);
	const [inviteError, setInviteError] = useState<string | null>(null);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [membershipToDelete, setMembershipToDelete] = useState<string | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);
	const [deleteType, setDeleteType] = useState<"member" | "invite">("invite");
	const { planDetails, getPlan } = usePlan();
	const { selectedBrand } = useBrands();
	const { role: currentUserRole } = usePermission();

	// Get plan limit for team members
	const allowTeamMembers = planDetails?.plan?.features?.team_members ?? true;
	
	// Check if team invites are blocked by plan
	const blockedByPlan = !allowTeamMembers;

	// Get brand URL and user role from selected brand
	const brandUrl = selectedBrand?.slug;
	const userRole = currentUserRole ?? selectedBrand?.role ?? null;

	useEffect(() => {
		const loadMemberships = async () => {
			if (currentWorkspaceId) {
				setIsLoading(true);
				const result = await indexMembership(currentWorkspaceId);
				setIsLoading(false);
				if (result?.success && result.data && result.data.length > 1) {
					const store = useOnboardingStore.getState();
					if (!store.completedSteps.includes("invite_team_members")) {
						store.completeStep(currentWorkspaceId, "invite_team_members");
					}
				}
			}
		};

		loadMemberships();
	}, [indexMembership, currentWorkspaceId]);

	// Load plan details
	useEffect(() => {
		if (currentWorkspaceId) {
			getPlan(currentWorkspaceId);
		}
	}, [currentWorkspaceId, getPlan]);

	const handleDeleteMember = async (id: string | null) => {
		setMembershipToDelete(id);
		setDeleteType("member");
		setDeleteModalOpen(true);
	};

	const handleDeleteInvite = async (id: string | null) => {
		setMembershipToDelete(id);
		setDeleteType("invite");
		setDeleteModalOpen(true);
	};

	const confirmDelete = async () => {
		if (!currentWorkspaceId || !membershipToDelete) return;

		setIsDeleting(true);
		const result = await deleteMembership(membershipToDelete, currentWorkspaceId);
		setIsDeleting(false);

		if (result.success) {
			setDeleteModalOpen(false);
			setMembershipToDelete(null);
			showToast({
				title: deleteType === "invite" ? "Invite deleted" : "Member removed",
				description: deleteType === "invite" ? "The invitation has been deleted successfully." : "The member has been removed from the team.",
				status: "success",
				duration: 5000,
			});
		} else {
			// Handle error if needed
			setDeleteModalOpen(false);
			setMembershipToDelete(null);
		}
	};

	const handleResendInvite = async (id: string) => {
		if (!currentWorkspaceId) return;

		const result = await resendMembershipInvitation(id, currentWorkspaceId);

		if (result.success) {
			// Successfully deleted
		} else {
			// Handle error if needed
		}
	};

	const handleInviteUser = async (data: MembershipRequest) => {
		if (currentWorkspaceId) {
			setInviteIsLoading(true);
			setInviteError(null);
			const result = await createMembership(currentWorkspaceId, data);
			setInviteIsLoading(false);

			if (result.success) {
				useOnboardingStore.getState().completeStep(currentWorkspaceId, "invite_team_members");
				// Success - form will be cleared by the component
				showToast({
					title: "Invitation sent!",
					description: `An invitation has been sent to ${data.email}`,
					status: "success",
					duration: 5000,
				});
				return result;
			} else {
				// Error - set error message
				if (result.message) {
					setInviteError(result.message);
				}
				return null;
			}
		}
		return null;
	};

	return (
		<>
			<DeleteConfirmationModal
				open={deleteModalOpen}
				onOpenChange={setDeleteModalOpen}
				onConfirm={confirmDelete}
				isLoading={isDeleting}
				title={deleteType === "invite" ? "Delete Invite" : "Remove Member"}
				description={
					deleteType === "invite"
						? "Are you sure you want to delete this invitation? This action cannot be undone."
						: "Are you sure you want to remove this member from the team? This action cannot be undone."
				}
			/>
			<Container className="py-12 space-y-8">
				{/* Page Header */}
				<div>
					<h1 className="text-xl font-semibold text-primary">Teams</h1>
					<p className="mt-3 text-tertiary max-w-[640px]">Manage your team members and invite collaborators to your workspace.</p>
				</div>

				{/* Team Members Section */}
				<section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
					<div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
						<div>
							<h2 className="text-base font-semibold text-primary">Team Members</h2>
							<p className="text-sm text-tertiary mt-1">People who have access to this workspace.</p>
						</div>
						{memberships && memberships.length > 0 && (
							<span className="inline-flex items-center justify-center w-6 h-6 text-xs font-medium rounded-full bg-gray-200 text-gray-600">{memberships.length}</span>
						)}
					</div>

					{isLoading && (
						<div className="p-5">
							<MembershipListSkeleton />
						</div>
					)}
				{!isLoading && memberships !== null && memberships.length > 0 && (
					<MembershipList 
						memberships={memberships} 
						onDelete={handleDeleteMember} 
						blockedByPlan={blockedByPlan}
						brandUrl={brandUrl}
						userRole={userRole}
					/>
				)}
					{memberships && memberships.length === 0 && !isLoading && <div className="text-sm text-tertiary py-8 text-center">No members found. Invite your team to collaborate!</div>}
				</section>

				{/* Invites Section */}
				{invites && invites.length > 0 && (
					<section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
						<div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
							<div>
								<h2 className="text-base font-semibold text-primary">Pending Invites</h2>
								<p className="text-sm text-tertiary mt-1">Invitations that are waiting to be accepted.</p>
							</div>
							<span className="inline-flex items-center justify-center w-6 h-6 text-xs font-medium rounded-full bg-amber-100 text-amber-700">{invites.length}</span>
						</div>

						{isLoading && (
							<div className="p-5">
								<InviteListSkeleton />
							</div>
						)}
						{!isLoading && (
					<InviteList 
						memberships={invites} 
						onDelete={handleDeleteInvite} 
						onResendInvite={handleResendInvite}
						blockedByPlan={blockedByPlan}
						brandUrl={brandUrl}
						userRole={userRole}
					/>
				)}
					</section>
				)}

				{/* Invite Form Section */}
				{(allowTeamMembers || blockedByPlan) && (
					<section className="bg-white rounded-xl border border-gray-200 overflow-hidden">
						<div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50">
							<h2 className="text-base font-semibold text-primary">Invite New Member</h2>
							<p className="text-sm text-tertiary mt-1">Send an invitation to add someone to your team.</p>
						</div>

						<div className="p-5 space-y-4">
							<CreateInviteForm onSubmit={handleInviteUser} isLoading={inviteIsLoading} error={inviteError} onErrorChange={setInviteError} disabled={blockedByPlan} />
							{blockedByPlan && <TeamPlanRestrictionCallout userRole={userRole} workspaceId={currentWorkspaceId} />}
						</div>
					</section>
				)}
			</Container>
		</>
	);
}
